import React, { useState } from 'react';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDraggable } from '@dnd-kit/core';
import { OBJECT_CATEGORIES, ObjectDefinition } from '../../constants';
import { useSceneStore } from '../../stores';
import { cn } from '@/lib/utils';

interface DraggableObjectProps {
  definition: ObjectDefinition;
}

const DraggableObject: React.FC<DraggableObjectProps> = ({ definition }) => {
  const { addObject } = useSceneStore();
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: definition.type,
    data: definition,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 1000 : 1,
      }
    : undefined;

  const handleAddToScene = () => {
    const newObject = {
      type:
        definition.type === 'box' ||
        definition.type === 'sphere' ||
        definition.type === 'cylinder' ||
        definition.type === 'plane'
          ? ('primitive' as const)
          : ('device' as const),
      name: definition.name,
      position: [0, 1, 0] as [number, number, number],
      properties: definition.defaultProperties,
    };

    addObject(newObject);
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={cn(
        'bg-gray-100 dark:bg-gray-700 p-3 rounded-lg cursor-move hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors border',
        isDragging && 'opacity-50 border-blue-500',
      )}
      title={definition.description}
    >
      <div className="text-2xl mb-2 text-center">{definition.icon}</div>
      <div className="text-xs text-center font-medium mb-2">{definition.name}</div>
      <Button
        size="sm"
        variant="outline"
        className="w-full text-xs h-6"
        onClick={(e) => {
          e.stopPropagation();
          handleAddToScene();
        }}
      >
        Add
      </Button>
    </div>
  );
};

interface ObjectCategoryProps {
  category: { name: string; items: ObjectDefinition[] };
  searchTerm: string;
}

const ObjectCategory: React.FC<ObjectCategoryProps> = ({ category, searchTerm }) => {
  const filteredItems = category.items.filter(
    (item) =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.description.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (filteredItems.length === 0) return null;

  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">{category.name}</h3>
      <div className="grid grid-cols-2 gap-3">
        {filteredItems.map((item) => (
          <DraggableObject key={item.type} definition={item} />
        ))}
      </div>
    </div>
  );
};

const QuickActions: React.FC = () => {
  const { addObject } = useSceneStore();

  const quickItems = [
    {
      name: 'Temperature Sensor',
      icon: '🌡️',
      action: () =>
        addObject({
          type: 'device',
          name: 'Temperature Sensor',
          position: [0, 1, 0],
          properties: {
            deviceType: 'temperature-sensor',
            status: 'online',
            color: '#ff6b6b',
          },
        }),
    },
    {
      name: 'Motion Sensor',
      icon: '🏃',
      action: () =>
        addObject({
          type: 'device',
          name: 'Motion Sensor',
          position: [2, 1, 0],
          properties: {
            deviceType: 'motion-sensor',
            status: 'online',
            color: '#45b7d1',
          },
        }),
    },
    {
      name: 'Basic Box',
      icon: '📦',
      action: () =>
        addObject({
          type: 'primitive',
          name: 'Box',
          position: [0, 0.5, 2],
          properties: {
            geometry: 'box',
            material: { color: '#95a5a6' },
          },
        }),
    },
  ];

  return (
    <div className="p-4 space-y-3">
      <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Quick Add</h3>
      <div className="space-y-2">
        {quickItems.map((item, index) => (
          <Button key={index} variant="outline" size="sm" className="w-full justify-start h-10" onClick={item.action}>
            <span className="text-lg mr-2">{item.icon}</span>
            <span className="text-xs">{item.name}</span>
          </Button>
        ))}
      </div>
    </div>
  );
};

export const ObjectLibrary: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');

  return (
    <div className="w-64 bg-white dark:bg-gray-800 h-full flex flex-col border-r">
      <div className="p-4 border-b">
        <h2 className="text-lg font-semibold mb-3">Object Library</h2>
        <Input
          placeholder="Search objects..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full"
        />
      </div>

      <Tabs defaultValue="library" className="flex-1 flex flex-col">
        <TabsList className="grid w-full grid-cols-2 mx-4 mt-2">
          <TabsTrigger value="library">Library</TabsTrigger>
          <TabsTrigger value="quick">Quick</TabsTrigger>
        </TabsList>

        <TabsContent value="library" className="flex-1 mt-2">
          <ScrollArea className="h-full">
            <div className="p-4 space-y-4">
              {OBJECT_CATEGORIES.map((category) => (
                <ObjectCategory key={category.name} category={category} searchTerm={searchTerm} />
              ))}
            </div>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="quick" className="flex-1">
          <QuickActions />
        </TabsContent>
      </Tabs>
    </div>
  );
};
